import React, { useState } from "react";
import { connect } from "react-redux";
import {
  Button,
  Stack,
  Grid,
  Box,
  Paper,
  IconButton,
  Typography,
} from "@mui/material";
import { Link, NavLink } from "react-router-dom";

import ChatIcon from "@mui/icons-material/Chat";
import ConstructionIcon from "@mui/icons-material/Construction";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SavingsIcon from "@mui/icons-material/Savings";

// local imports
import FooterPage from "./Footer";
import { CommentPage } from "./Comments";
import NewsFeed from "./NewsFeed";
import MarketSummary from "./MarketSummary";
import { MakeCard, MonitorCard, WatchCard } from "../customeCards/HomeCards";

const AboutPage = ({ isAuthenticated, publicKey }) => {
  const [showCanvas, setShowCanvas] = useState(false);
  const openCanvas = () => setShowCanvas(true);
  const closeCanvas = () => setShowCanvas(false);

  const candlLink = (
    <a
      href="https://solscan.io/token/7e2CmG7GSPpK1w6iDuti5UZzg4hwyNhiGk1sFjKjrskd"
      target="_blank"
      rel="noopener noreferrer"
    >
      CANDL
    </a>
  );
  const candle10 = <span style={{ fontSize: "1.1em", color: "#0d6efd" }}>10 CANDL</span>;

  return (
    <Box className="about-page">
      <Grid container spacing={0} alignItems="stretch" sx={{ mt: 0 }}>
        {/* Main column (9/12) */}
        <Grid item xs={12} md={9}>
          <Stack spacing={2}>
            {/* Hero */}
            <Paper elevation={0} sx={{ p: 6, backgroundColor: "lightgray" }}>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography variant="h3" fontWeight="bold">
                  Welcome to Watchlist101
                </Typography>
                <Typography variant="h6" fontStyle="italic">
                  "the social watchlist"
                </Typography>
              </Box>

              <Stack spacing={2} sx={{ maxWidth: 900, mx: "auto" }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <IconButton disabled>
                    <ConstructionIcon fontSize="large" sx={{ color: "blue" }} />
                  </IconButton>
                  <Typography variant="subtitle1">Create private and public watchlists</Typography>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                  <IconButton disabled>
                    <TrendingUpIcon fontSize="large" sx={{ color: "green" }} />
                  </IconButton>
                  <Typography variant="subtitle1">Monitor their performance</Typography>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                  <IconButton disabled>
                    <SavingsIcon fontSize="large" sx={{ color: "orange" }} />
                  </IconButton>
                  <Typography variant="subtitle1">Earn rewards</Typography>
                </Stack>
              </Stack>
            </Paper>

            {/* Cards */}
            <Box sx={{ backgroundColor: "#E5E4E2", py: 1 }}>
              <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ px: 1 }}>
                <MakeCard />
                <MonitorCard />
                <WatchCard />
              </Stack>
            </Box>

            {/* Token rewards */}
            <Paper elevation={0} sx={{ p: 4, backgroundColor: "lightgray" }}>
              <Typography variant="h6">Token rewards distribution:</Typography>
              <Box sx={{ pl: 2, mt: 1 }}>
                <Typography variant="body2" paragraph>
                  Candle coin {candlLink} is a cryptocurrency token on Solana blockchain.
                </Typography>
                <Typography variant="body2" paragraph>
                  Sign up and connect your wallet: {candle10}
                </Typography>
                <Typography variant="body2" paragraph>
                  Create a public watchlist: {candle10}
                </Typography>
                <Typography variant="body2">
                  Public watchlists outperforming <em>S&P 500</em> will receive more tokens over time.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                {publicKey ? null : isAuthenticated ? (
                  <NavLink to="/connect-wallet">
                    <Button variant="contained" color="primary">
                      Connect Wallet
                    </Button>
                  </NavLink>
                ) : (
                  <Link to={{ pathname: "/login", state: { fromHome: true } }}>
                    <Button variant="contained" color="primary">
                      Login &amp; Connect Wallet
                    </Button>
                  </Link>
                )}
              </Box>
            </Paper>

            {/* Leaderboard */}
            <Paper elevation={0} sx={{ p: 4, backgroundColor: "#E5E4E2" }}>
              <Typography variant="h6">
                <NavLink to="/leaderboard">LEADERBOARD</NavLink>
              </Typography>
              <Box sx={{ pl: 2, mt: 1 }}>
                <Typography variant="body2" paragraph>
                  View public watchlists and their performance compared to market benchmarks.
                </Typography>
                <Typography variant="body2">
                  Share watchlists on social media or download a copy. Each watchlist's performance is measured from when it was saved.
                </Typography>
              </Box>
            </Paper>

            {/* Markets */}
            <Paper elevation={0} sx={{ p: 4, backgroundColor: "lightgray" }}>
              <Typography variant="h6">Available Markets:</Typography>
              <Box sx={{ pl: 2, mt: 1 }}>
                <Typography variant="body2" paragraph>
                  S&amp;P 500 components, Nasdaq 100 components, Dow 30 components, and top 100 cryptocurrencies by market cap.
                </Typography>
                <Typography variant="body2" paragraph>
                  S&amp;P 500 sector components with organized lists.
                </Typography>
                <Typography variant="body2">
                  Popular, sector, commodity, currency, country, volatility, and index ETFs.
                </Typography>
              </Box>
            </Paper>

            {/* Market summary */}
            <Box sx={{ backgroundColor: "#E5E4E2", p: 0 }}>
              <MarketSummary />
            </Box>
          </Stack>
        </Grid>

        {/* Sidebar (3/12) */}
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            display: "flex",
            flexDirection: "column",
            position: { xs: "relative", md: "sticky" },
            top: { md: 0 },
            height: { md: "100vh" },
            overflowY: "auto",
            backgroundColor: "#f7f7f7",
            borderLeft: "1px solid #e0e0e0",
            px: 0,
            pl:1
          }}
        >
          <NewsFeed />
        </Grid>
      </Grid>

      <FooterPage />

      {/* Floating chat */}
      <IconButton
        onClick={openCanvas}
        sx={{
          position: "fixed",
          right: 16,
          bottom: 16,
          bgcolor: "orange",
          color: "white",
          "&:hover": { bgcolor: "darkorange" },
        }}
        aria-label="open chat"
      >
        <ChatIcon fontSize="large" />
      </IconButton>

      <CommentPage fullUrl={window.location.href} identifier="about" showCanvas={showCanvas} closeCanvas={closeCanvas} />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  publicKey: state.auth.publicKey,
  watchlist: state.auth.watchlistData,
});

export default connect(mapStateToProps)(AboutPage);
